export const SEVERITY_ORDER = {
  Catastrophique: 6,
  Critique: 5,
  Majeur: 4,
  'Modéré': 3,
  Mineur: 2,
  'Non défini': 1
};

const stripAccents = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

export const normalizeSeverity = (raw) => {
  const normalized = stripAccents(raw).trim().toLowerCase();
  if (!normalized) {
    return 'Non défini';
  }
  if (normalized === 'catastrophique' || normalized === 'catastrophic') {
    return 'Catastrophique';
  }
  if (normalized === 'critique' || normalized === 'critical') {
    return 'Critique';
  }
  if (normalized === 'majeur' || normalized === 'major') {
    return 'Majeur';
  }
  if (normalized === 'modere' || normalized === 'moderate') {
    return 'Modéré';
  }
  if (normalized === 'mineur' || normalized === 'minor') {
    return 'Mineur';
  }
  return 'Non défini';
};

export const normalizePillar = (raw) => {
  const value = String(raw || '').trim();
  if (!value) {
    return '';
  }
  const lowered = stripAccents(value).toLowerCase();
  if (lowered === 'auditabilite') {
    return 'Auditabilité';
  }
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
};

const isImplemented = (control) => {
  if (!control || typeof control !== 'object') {
    return false;
  }
  const status = stripAccents(control.status).trim().toLowerCase();
  return status === 'implemented' || status === 'implemente';
};

const getEstimatedBucket = (control) => {
  const priority = stripAccents(control.priority).trim().toLowerCase();
  const status = stripAccents(control.status).trim().toLowerCase();
  if (priority === 'high' || status === 'apply_before') {
    return 'shortTerm';
  }
  if (priority === 'medium' || status === 'planned') {
    return 'mediumTerm';
  }
  if (priority === 'low' || status === 'partial') {
    return 'longTerm';
  }
  return 'mediumTerm';
};

export const splitTimelineBuckets = (controlsData, now) => {
  const reference = now instanceof Date ? now.getTime() : new Date(now || Date.now()).getTime();
  const buckets = {
    shortTerm: { dated: 0, estimated: 0 },
    mediumTerm: { dated: 0, estimated: 0 },
    longTerm: { dated: 0, estimated: 0 }
  };
  if (!Array.isArray(controlsData)) {
    return buckets;
  }
  const threeMonths = 90 * 24 * 60 * 60 * 1000;
  const twelveMonths = 365 * 24 * 60 * 60 * 1000;
  controlsData.forEach((control) => {
    if (!control || isImplemented(control)) {
      return;
    }
    if (control.implementationDate) {
      const target = new Date(control.implementationDate).getTime();
      if (Number.isNaN(target)) {
        buckets[getEstimatedBucket(control)].estimated += 1;
        return;
      }
      const diff = target - reference;
      if (diff <= threeMonths) {
        buckets.shortTerm.dated += 1;
      } else if (diff <= twelveMonths) {
        buckets.mediumTerm.dated += 1;
      } else {
        buckets.longTerm.dated += 1;
      }
      return;
    }
    buckets[getEstimatedBucket(control)].estimated += 1;
  });
  return buckets;
};

export const computeMaturity = (controlsData, events) => {
  const controls = Array.isArray(controlsData) ? controlsData : [];
  const totalControls = controls.length;
  const implementedCount = controls.filter((control) => isImplemented(control)).length;
  const implementedPct = totalControls === 0 ? 0 : Math.round((implementedCount / totalControls) * 100);
  const eventsTotal = Array.isArray(events) ? events.length : 0;
  return {
    totalControls,
    implementedCount,
    implementedPct,
    eventsTotal
  };
};

export const resolveEventBusinessValueKey = (event) => {
  const raw = event?.businessValue;
  if (raw && typeof raw === 'object') {
    return String(raw.id ?? raw.name ?? '');
  }
  return String(raw ?? '');
};

export const buildBusinessValuePillarMatrix = (businessValues, dreadedEvents, pillars) => {
  const rows = Array.isArray(businessValues) ? businessValues : [];
  const columns = Array.isArray(pillars) ? pillars : [];
  const events = Array.isArray(dreadedEvents) ? dreadedEvents : [];
  const data = [];
  let max = 0;
  rows.forEach((bv, rowIndex) => {
    const bvKey = String(bv?.id ?? '');
    const bvName = String(bv?.name ?? '');
    columns.forEach((pillar, colIndex) => {
      const target = normalizePillar(pillar?.name ?? pillar?.id ?? pillar);
      const count = events.filter((event) => {
        const eventKey = resolveEventBusinessValueKey(event);
        const matchesBv = eventKey !== '' && (eventKey === bvKey || eventKey === bvName);
        return matchesBv && normalizePillar(event?.securityPillar) === target;
      }).length;
      if (count > max) {
        max = count;
      }
      data.push([colIndex, rowIndex, count]);
    });
  });
  return {
    rowLabels: rows.map((bv) => String(bv?.name ?? '')),
    columnLabels: columns.map((pillar) => String(pillar?.name ?? pillar?.id ?? '')),
    data,
    max
  };
};
